import type { CursorPage, HomeResponse, HomeSuggestion, InitializationState, Relationship, TalkAboutPersonSuggestionPayload } from '@mira/clients-shared';
import { createWebApiClient } from '../../api/webSession.js';
import { hrefForRoute, navigateTo } from '../../app/router.js';
import { showToast } from '../../app/toast.js';
import { Avatar, ButtonLink, EmptyState, ErrorState, IconGlyph, LoadingState, MiraIcon, Sheet } from '../../ui/primitives/index.js';
import { BrandWord } from '../auth/AuthScreens.js';
import { sampleRelationships } from '../reference-preview-data.js';
import { useApiQuery } from '../api-state.js';
import { useState, type FormEvent } from 'react';

export function TodayScreen(props: { preview?: boolean; screenId?: string; showAuthGate?: boolean; showRelationshipSheet?: boolean }) {
  const query = useApiQuery<HomeResponse>({
    preview: Boolean(props.preview),
    previewData: {
      displayName: 'Alex',
      initialization: { status: 'ready', guidance_mode: false },
      recentTopics: [{ id: 'communication', title: 'The conversation we did not finish', summary: 'You want to be heard, and you are looking for a way to say it without hurting.' }],
      homeSuggestions: [{ id: 'preview-suggestion', suggestion_kind: 'unfinished', title: 'Pick that conversation back up' }],
    },
    emptyData: { recentTopics: [], homeSuggestions: [] },
    previewError: 'Today could not be opened right now. Please try again.',
    previewState: todayPreviewState(props.screenId),
    load: () => createWebApiClient().home(),
    deps: [],
  });
  const [draft, setDraft] = useState('');
  const suggestions = query.data.homeSuggestions || [];
  const recentTopic = query.data.recentTopics?.[0];

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    navigateTo('chat', { conversationId: 'new' }, draft.trim() ? { source: 'today', draft: draft.trim(), autostart: '1' } : { source: 'today' });
  }

  return (
    <article className="mira-today-screen mira-botanical-page">
      <header className="mira-page-brand-row">
        <BrandWord />
        <a className="mira-help-button" href={hrefForRoute('privacy', {}, { source: 'today' })} aria-label="Privacy"><MiraIcon name="semantic.insight" /></a>
      </header>
      <section className="mira-today-hero">
        <h1>Hi{query.data.displayName ? `, ${query.data.displayName}` : ''}</h1>
        <p>In this moment, where do you want to start?</p>
      </section>
      {query.state === 'loading' ? <LoadingState title="Opening your space" /> : null}
      {query.state === 'error' ? <ErrorState title="Today could not load" body={query.error} /> : null}
      {query.state !== 'loading' && query.state !== 'error' ? (
        <section className="mira-today-entry-stack" aria-label="Start conversation">
          {query.data.initialization ? <InitializationNotice initialization={query.data.initialization} /> : null}
          <form className="mira-today-prompt-card" onSubmit={submit}>
            <label>
              <span>What's on your mind right now?</span>
              <div className="mira-start-textarea-shell">
                <textarea aria-label="What's on your mind right now?" value={draft} onChange={(event) => setDraft(event.currentTarget.value)} />
                <button aria-label="Start conversation" disabled={!draft.trim()} type="submit"><MiraIcon name="action.send" /></button>
              </div>
            </label>
          </form>
          <ButtonLink href="#/today?sheet=relationship" variant="secondary">Talk about a person</ButtonLink>
          {suggestions.length ? <div className="mira-starter-chip-list" aria-label="Continue from recent threads">
            {suggestions.slice(0, 3).map((suggestion) => suggestion.suggestion_kind === 'talk_about_person' && suggestion.id
              ? <RelationshipSuggestionAction key={suggestion.id} onResolved={query.reload} suggestion={suggestion} />
              : <a href={homeSuggestionHref(suggestion)} key={suggestion.id || suggestion.title}>{suggestion.title}</a>)}
          </div> : null}
          {recentTopic ? (
            <a className="mira-today-context-card" href={hrefForRoute('topic-detail', { topicId: recentTopic.id }, { source: 'today' })}>
              <small>Recent topic</small>
              <strong>{recentTopic.title}</strong>
              {recentTopic.summary ? <p>{recentTopic.summary}</p> : null}
            </a>
          ) : null}
          <section className="mira-today-privacy-card">
            <strong>This is your private space</strong>
            <p>Mira uses what you share here to understand the moment. Long-term understanding lives in Memory, where you can review it.</p>
          </section>
        </section>
      ) : null}
      <div className="mira-corner-leaves" aria-hidden="true" />
      <div className="mira-corner-leaves mira-corner-leaves-right" aria-hidden="true" />
      {props.showRelationshipSheet ? <TodayRelationshipSheet preview={props.preview} screenId={props.screenId} /> : null}
      {props.showAuthGate ? <TodayAuthGateSheet /> : null}
    </article>
  );
}

function RelationshipSuggestionAction(props: { onResolved: () => void; suggestion: HomeSuggestion }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const payload = props.suggestion.payload as TalkAboutPersonSuggestionPayload | undefined;
  const displayName = payload?.displayName || props.suggestion.title;
  const matches = payload?.matches || [];

  async function accept() {
    if (!props.suggestion.id || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await createWebApiClient().acceptRelationshipSuggestion(props.suggestion.id);
      showToast(`Added ${result.relationship.display_name}`);
      navigateTo('relationship-detail', { relationshipId: result.relationship.id });
    } catch {
      setError('This relationship could not be added right now. Please try again.');
      setBusy(false);
    }
  }

  async function choose(relationshipId: string) {
    if (!props.suggestion.id || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await createWebApiClient().chooseRelationshipSuggestion(props.suggestion.id, { relationshipId });
      showToast(`Linked “${displayName}” to ${result.relationship.display_name}`);
      navigateTo('relationship-detail', { relationshipId: result.relationship.id });
    } catch {
      setError('This name could not be confirmed right now. Please try again.');
      setBusy(false);
    }
  }

  async function dismiss() {
    if (!props.suggestion.id || busy) return;
    setBusy(true);
    setError('');
    try {
      await createWebApiClient().dismissRelationshipSuggestion(props.suggestion.id);
      props.onResolved();
    } catch {
      setError('This suggestion could not be dismissed right now. Please try again.');
      setBusy(false);
    }
  }

  return (
    <section className="mira-relationship-suggestion" aria-label={`Confirm relationship with ${displayName}`}>
      <strong>{props.suggestion.title || `Add ${displayName} to your relationships?`}</strong>
      {payload?.reason ? <p>{payload.reason}</p> : <p>Mira noticed this person in recent conversations and may matter to later understanding.</p>}
      {payload?.needsIdentityChoice ? (
        <div className="mira-inline-actions">
          {matches.map((match) => <button disabled={busy} key={match.relationshipId} onClick={() => choose(match.relationshipId)} type="button">Yes — {match.displayName}</button>)}
          <a href={hrefForRoute('relationship-new', {}, { displayName })}>None of these — new person</a>
        </div>
      ) : (
        <button disabled={busy} onClick={accept} type="button">{busy ? 'Working…' : 'Add to relationships'}</button>
      )}
      <button disabled={busy} onClick={dismiss} type="button">Not now</button>
      {error ? <p role="alert">{error}</p> : null}
    </section>
  );
}

function TodayRelationshipSheet(props: { preview?: boolean; screenId?: string }) {
  const [search, setSearch] = useState(props.screenId === 'ls_08_today_relationship_sheet_no_results' ? 'Sam' : '');
  const relationships = useApiQuery<CursorPage<Relationship>>({
    preview: Boolean(props.preview),
    previewData: { items: sampleRelationships },
    previewError: 'Relationships could not be loaded right now. Please try again.',
    previewState: relationshipSheetPreviewState(props.screenId),
    emptyData: { items: [] },
    load: () => createWebApiClient().relationships({ limit: 20 }),
    isEmpty: (value) => value.items.length === 0,
    deps: [],
  });
  const query = search.trim().toLowerCase();
  const visible = relationships.data.items.filter((relationship) =>
    !query || `${relationship.display_name} ${relationship.relationship_text || ''}`.toLowerCase().includes(query),
  );
  const createHref = hrefForRoute('relationship-new', {}, search.trim() ? { displayName: search.trim() } : {});
  return (
    <Sheet className="mira-relationship-picker-sheet" scrimClassName="mira-bottom-sheet-scrim" title="Choose a person">
        <span className="mira-sheet-handle" aria-hidden="true" />
        <h2>Who do you want to talk about?</h2>
        <p>Pick someone and this conversation will center on your relationship with them.</p>
        <label className="mira-picker-search">
          <span aria-hidden="true"><MiraIcon name="action.search" /></span>
          <input aria-label="Search relationships" onChange={(event) => setSearch(event.currentTarget.value)} placeholder="Search by name or relationship" type="search" value={search} />
          <strong aria-hidden="true"><IconGlyph value="☷" /></strong>
        </label>
        {relationships.state === 'loading' ? <LoadingState title="Loading relationships" /> : null}
        {relationships.state === 'error' ? <ErrorState title="Relationships could not be loaded" body={relationships.error} /> : null}
        {relationships.state === 'empty' ? <EmptyState title="No relationships yet" body="Add someone who matters first, then start conversations around that relationship." /> : null}
        {relationships.state === 'ready' ? (
          visible.length ? (
            <>
              <small>Recent</small>
              <div className="mira-picker-list">
                {visible.map((relationship) => (
                  <a href={hrefForRoute('chat', { conversationId: 'new' }, { source: 'today', relationshipId: relationship.id })} key={relationship.id}>
                    <Avatar decorative label={relationship.display_name} />
                    <span><strong>{relationship.display_name}</strong><em>{relationship.relationship_text || 'Important relationship'}{relationshipPreviewHint(relationship)}</em></span>
                    <b aria-hidden="true"><MiraIcon name="action.next" /></b>
                  </a>
                ))}
              </div>
            </>
          ) : <EmptyState title={`No matches for “${search.trim()}”`} />
        ) : null}
        <a className="mira-picker-create" href={createHref}><span aria-hidden="true"><IconGlyph value="＋" /></span>Add a person <b aria-hidden="true"><MiraIcon name="action.next" /></b></a>
        <a className="mira-picker-cancel" href={hrefForRoute('today')}>Cancel</a>
    </Sheet>
  );
}

function relationshipPreviewHint(relationship: Relationship): string {
  const source = relationship as Relationship & { relationship_context?: string | null };
  if (source.relationship_context?.toLowerCase().includes('trust')) return ' · trust';
  if (source.relationship_context?.toLowerCase().includes('boundaries')) return ' · boundaries';
  if (relationship.relationship_text?.toLowerCase().includes('family')) return ' · family';
  if (relationship.relationship_text?.toLowerCase().includes('friend')) return ' · friends';
  return '';
}

function todayPreviewState(screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (screenId === 'today_01_loading') return 'loading';
  if (screenId === 'today_02_error') return 'error';
  return undefined;
}

function relationshipSheetPreviewState(screenId?: string): 'loading' | 'empty' | 'error' | 'ready' | undefined {
  if (screenId === 'tr_02_relationship_sheet_loading') return 'loading';
  if (screenId === 'tr_03_relationship_sheet_error') return 'error';
  if (screenId === 'ls_07_today_relationship_sheet_empty') return 'empty';
  return undefined;
}

function TodayAuthGateSheet() {
  return (
    <Sheet className="mira-auth-gate-sheet" title="Sign in required">
        <span className="mira-auth-gate-icon" aria-hidden="true" />
        <h2>Sign in to continue</h2>
        <p>Sign in so Mira can link this conversation to your profile, memory, and relationships.</p>
        <a className="mira-auth-gate-primary" href={hrefForRoute('login', {}, { returnTo: '/today' })}>Sign in</a>
        <a className="mira-auth-gate-secondary" href={hrefForRoute('register', {}, { returnTo: '/today' })}>Create account</a>
        <a className="mira-auth-gate-cancel" href={hrefForRoute('today')}>Cancel</a>
    </Sheet>
  );
}

function InitializationNotice(props: { initialization: InitializationState }) {
  const status = props.initialization.status;
  if (status === 'ready') return null;
  const copy = status === 'provisional'
    ? { title: 'Birth date recorded', body: 'You can start chatting now. Adding birth time later deepens the understanding of Mira.' }
    : status === 'pending' || status === 'initializing'
      ? { title: 'Mira is still getting to know you', body: 'This does not block the conversation. You can start talking now.' }
      : status === 'partial'
        ? { title: 'Baseline understanding is partly ready', body: 'Keep chatting. Unfinished parts stay explicitly unfinished — never faked as ready.' }
        : { title: 'Baseline understanding is not ready yet', body: 'You can still start chatting, or save your birth details again later in Settings.' };
  return <aside className="mira-soft-note mira-initialization-notice" data-status={status}><span aria-hidden="true"><IconGlyph value="♧" /></span><p><strong>{copy.title}</strong>{copy.body}</p></aside>;
}

function homeSuggestionHref(suggestion: HomeSuggestion): string {
  const payload = suggestion.payload || {};
  const conversationId = typeof payload.conversationId === 'string' ? payload.conversationId : null;
  if (conversationId) return hrefForRoute('chat', { conversationId }, { source: 'today' });
  const relationshipId = typeof payload.relationshipId === 'string' ? payload.relationshipId : null;
  return hrefForRoute('chat', { conversationId: 'new' }, {
    source: 'today',
    draft: suggestion.title,
    autostart: '1',
    ...(relationshipId ? { relationshipId } : {}),
  });
}
